import React, { useEffect, useState } from "react";
import { Modal, Button, Badge, Spinner } from "react-bootstrap";
import { testIntegration } from "../../services/RepositoryService";

function statusVariant(code) {
    if (!code) return "secondary";
    if (code >= 200 && code < 300) return "success";
    if (code >= 400) return "danger";
    return "warning";
}

const IntegrationTestModal = ({ show, onHide, onConnect, integrationData }) => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const runTest = async () => {
        setLoading(true);
        setResult(null);
        try {
            const res = await testIntegration(integrationData);
            setResult(res);
        } catch (err) {
            setResult({ error_message: err?.message || "Request failed", status_code: null, duration_ms: null });
        } finally {
            setLoading(false);
        }
    };

    // Auto-run when modal opens
    useEffect(() => {
        if (show) {
            setResult(null);
            runTest();
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleConnect = async () => {
        // Let the sidebar's save + close flow handle dismissal on success;
        // if validation fails it shows an error toast and the modal stays open.
        if (onConnect) await onConnect();
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-bolt me-2 text-warning" />
                    Test Integration
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading && (
                    <div className="d-flex align-items-center gap-2 text-muted py-2">
                        <Spinner animation="border" size="sm" />
                        <span>Sending request…</span>
                    </div>
                )}

                {!loading && result && (
                    <>
                        <div className="d-flex align-items-center gap-4 mb-3">
                            <div>
                                <span className="text-muted small me-2">Status</span>
                                {result.status_code ? (
                                    <Badge bg={statusVariant(result.status_code)} className="fs-6 px-2">
                                        {result.status_code}
                                    </Badge>
                                ) : (
                                    <Badge bg="danger" className="fs-6 px-2">No Response</Badge>
                                )}
                            </div>
                            {result.duration_ms != null && (
                                <div>
                                    <span className="text-muted small me-1">Duration</span>
                                    <span className="fw-semibold">{result.duration_ms} ms</span>
                                </div>
                            )}
                        </div>

                        {result.error_message && (
                            <div className="alert alert-danger py-2 px-3 mb-3 small">
                                <i className="fa fa-exclamation-triangle me-2" />
                                {result.error_message}
                            </div>
                        )}

                        <p className="text-muted small mb-1">Connection Log</p>
                        <pre
                            className="bg-light border rounded p-3 log-preview max-height-260px font-size-13px"
                        >
                            {result.response_body ?? "(no response body)"}
                        </pre>
                    </>
                )}

                {!loading && !result && (
                    <div className="text-muted text-center py-4">No result yet.</div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
                    Close
                </Button>
                <Button variant="outline-primary" onClick={runTest} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                            Retrying…
                        </>
                    ) : (
                        <><i className="fa fa-redo me-1" />Retry</>
                    )}
                </Button>
                <Button variant="success" onClick={handleConnect} disabled={loading}>
                    <i className="fa fa-plug me-1" />Connect
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default IntegrationTestModal;
